import { sleep } from "@redotech/util/schedule";
import { useRef, useState } from "react";
import { useHandler } from "./hook";
import { useTriggerLoad } from "./load";

export function usePendingUpdates<T extends object>({
  updateGroups,
  identifier,
}: {
  updateGroups: (groups: T[]) => Promise<void>;
  identifier: (group: T) => string;
}) {
  const pendingUpdatesRef = useRef<{
    updates: T[];
    currentPromise: Promise<void> | null;
  }>({ updates: [], currentPromise: null });
  const [updatesPending, setUpdatesPending] = useState(false);

  const hasPendingUpdate = useHandler((id: string) => {
    return pendingUpdatesRef.current.updates.some(
      (update) => identifier(update) === id,
    );
  });

  const addPendingUpdates = useHandler((updates: T[]) => {
    pendingUpdatesRef.current.updates.push(...updates);
    doUpdates();
  });

  const [__, doUpdates] = useTriggerLoad(async () => {
    // Wait to obtain "lock" on the pending updates
    while (pendingUpdatesRef.current.currentPromise) {
      try {
        await pendingUpdatesRef.current.currentPromise;
      } finally {
        // noop
      }
    }
    if (pendingUpdatesRef.current.updates.length === 0) {
      return;
    }
    setUpdatesPending(true);
    pendingUpdatesRef.current.currentPromise = (async () => {
      const numUpdates = pendingUpdatesRef.current.updates.length;
      await sleep(Temporal.Duration.from({ milliseconds: 500 }));
      // If updates have come in since the request was made, debounce
      if (numUpdates !== pendingUpdatesRef.current.updates.length) {
        return;
      }
      const fullUpdates = pendingUpdatesRef.current.updates.slice();
      const updateMap = fullUpdates.reduce((map: Record<string, T>, update) => {
        const existing = map[identifier(update)];
        if (!existing) {
          map[identifier(update)] = update;
        } else {
          Object.assign(existing, update);
        }
        return map;
      }, {});
      const updates = Object.values(updateMap);
      try {
        await updateGroups(updates);
      } finally {
        pendingUpdatesRef.current.updates.splice(0, fullUpdates.length);
      }
    })();

    try {
      await pendingUpdatesRef.current.currentPromise;
    } finally {
      pendingUpdatesRef.current.currentPromise = null;
      if (pendingUpdatesRef.current.updates.length === 0) {
        setUpdatesPending(false);
      }
    }
  });

  return { updatesPending, hasPendingUpdate, addPendingUpdates };
}
